/**
 * BrandingContext — brand asset URLs served from object storage (MinIO via the
 * backend ``/api/me`` ``branding`` field), injected once at the app root.
 *
 * Each value is a URL string; an empty string ("") means "not configured", in which
 * case consumers fall back to their built-in glyphs. No brand PNGs are bundled — the
 * images live in object storage and are fetched at runtime from these URLs.
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";

import type { CurrencyKind } from "../components/CurrencyIcon";

/** Brand asset URLs (empty string = unset). Mirrors ``/api/me`` ``branding``. */
export interface Branding {
  readonly logo: string;
  readonly mora: string;
  readonly primogem: string;
  readonly glitter: string;
  readonly topups: string;
}

/** Empty branding — every asset unset (consumers use their built-in fallback). */
export const EMPTY_BRANDING: Branding = {
  logo: "",
  mora: "",
  primogem: "",
  glitter: "",
  topups: "",
};

const BrandingCtx = createContext<Branding>(EMPTY_BRANDING);

export interface BrandingProviderProps {
  /** Brand asset URLs; omitted/empty keys fall back to built-in glyphs. */
  readonly value: Partial<Branding>;
  readonly children: ReactNode;
}

export function BrandingProvider({
  value,
  children,
}: BrandingProviderProps): JSX.Element {
  const merged: Branding = { ...EMPTY_BRANDING, ...value };
  return <BrandingCtx.Provider value={merged}>{children}</BrandingCtx.Provider>;
}

/** Read the current branding URLs (empty strings when no provider / unset). */
export function useBranding(): Branding {
  return useContext(BrandingCtx);
}

/** Convenience: the configured URL for a currency kind, or "" if unset. */
export function useCurrencyBrandUrl(kind: CurrencyKind): string {
  const branding = useBranding();
  return branding[kind];
}
